/**
 * ImageLightbox.tsx
 *
 * A reusable modal component for viewing full-size images.
 * Used for displaying image previews in expanded view.
 */

// 1. External dependencies
import React from 'react';
import { X } from 'lucide-react';

// 2. Internal absolute imports
import { Modal, ModalContent, Button } from '@enfomentis/ui';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for ImageLightbox component
 */
export interface ImageLightboxProps {
  /** Controls modal visibility */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Object URL or data URL to display */
  imageUrl: string | null;
  /** Alt text for accessibility (defaults to "Full size image") */
  alt?: string;
}

// =============================================================================
// Component
// =============================================================================

/**
 * ImageLightbox Component
 *
 * Displays a full-size image in a modal overlay with a close button.
 * Supports keyboard (Escape) and click-outside dismissal via HeroUI Modal.
 */
export const ImageLightbox: React.FC<ImageLightboxProps> = ({
  isOpen,
  onClose,
  imageUrl,
  alt = 'Full size image',
}) => {
  // Don't render modal if no image URL - prevents blank overlay
  if (!imageUrl) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      backdrop="blur"
      placement="center"
      hideCloseButton
    >
      <ModalContent className="bg-transparent shadow-none">
        <div className="relative flex items-center justify-center p-4">
          {/* Close button */}
          <Button
            isIconOnly
            variant="flat"
            size="sm"
            onPress={onClose}
            aria-label="Close image viewer"
            className="absolute top-2 right-2 z-10 bg-background/80 backdrop-blur-sm"
          >
            <X className="w-5 h-5" />
          </Button>

          {/* Image display */}
          <img
            src={imageUrl}
            alt={alt}
            className="max-h-[80vh] max-w-full object-contain rounded-lg"
          />
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ImageLightbox;
